// src/utils/annotation.ts — SCE Issues
// Formes de l'annotation des photos (components/files/EditeurAnnotation) :
// saisir une forme du doigt, la déplacer, l'agrandir ou la réduire, la faire
// pivoter (retours du 09/10). Tout est en pixels de la photo.
//
// Une forme garde ses points « à plat » ; son orientation (`angle`, en
// radians) s'applique autour du centre de son cadre. Agrandir une forme
// écarte ses points de ce centre ; un texte, lui, change de taille.

export type Point = { x: number; y: number }

export type Forme =
  | { outil: 'fleche' | 'cercle' | 'trait'; couleur: string; points: Point[]; angle?: number }
  | { outil: 'texte'; couleur: string; points: [Point]; texte: string; taille: number; angle?: number }

export interface Boite {
  x: number
  y: number
  w: number
  h: number
}

/** Taille par défaut d'un texte posé : lisible même sur une photo peu définie. */
export function tailleTexte(largeur: number, hauteur: number, epaisseur: number): number {
  return Math.round(Math.max(epaisseur * 8, Math.max(largeur, hauteur) / 20))
}

/** Cadre de la forme, à plat (avant orientation). `largeurTexte` : largeur mesurée du texte. */
export function boite(f: Forme, largeurTexte = 0): Boite {
  if (f.outil === 'texte') {
    const p = f.points[0]
    return { x: p.x, y: p.y - f.taille / 2, w: largeurTexte, h: f.taille }
  }
  const xs = f.points.map((p) => p.x)
  const ys = f.points.map((p) => p.y)
  const x = Math.min(...xs)
  const y = Math.min(...ys)
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y }
}

/** Centre du cadre : l'axe de rotation et d'agrandissement. */
export function centre(f: Forme, largeurTexte = 0): Point {
  const b = boite(f, largeurTexte)
  return { x: b.x + b.w / 2, y: b.y + b.h / 2 }
}

/** Fait tourner un point autour d'un centre. */
export function tourner(p: Point, c: Point, angle: number): Point {
  if (!angle) return p
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  const dx = p.x - c.x
  const dy = p.y - c.y
  return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos }
}

function distanceSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const l2 = dx * dx + dy * dy
  const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

/**
 * Le doigt touche-t-il la forme ? Flèche et trait : près du tracé. Cercle :
 * sur le tracé ou dedans (plus facile au doigt). Texte : dans son cadre.
 * L'orientation est prise en compte.
 */
export function touche(f: Forme, doigt: Point, tolerance: number, largeurTexte = 0): boolean {
  // Le doigt ramené « à plat », dans le repère de la forme.
  const p = tourner(doigt, centre(f, largeurTexte), -(f.angle ?? 0))
  if (f.outil === 'texte') {
    const b = boite(f, largeurTexte)
    return p.x >= b.x - tolerance && p.x <= b.x + b.w + tolerance && p.y >= b.y - tolerance && p.y <= b.y + b.h + tolerance
  }
  if (f.outil === 'cercle') {
    const [a, b] = [f.points[0]!, f.points[f.points.length - 1]!]
    const rx = Math.abs(b.x - a.x) / 2
    const ry = Math.abs(b.y - a.y) / 2
    const cx = (a.x + b.x) / 2
    const cy = (a.y + b.y) / 2
    if (rx < 1 || ry < 1) return Math.hypot(p.x - cx, p.y - cy) <= tolerance
    const n = Math.hypot((p.x - cx) / rx, (p.y - cy) / ry)
    return n <= 1 || (n - 1) * Math.min(rx, ry) <= tolerance
  }
  for (let i = 1; i < f.points.length; i++) {
    if (distanceSegment(p, f.points[i - 1]!, f.points[i]!) <= tolerance) return true
  }
  return f.points.length === 1 && Math.hypot(p.x - f.points[0]!.x, p.y - f.points[0]!.y) <= tolerance
}

/** La forme la plus haute (dernière dessinée) sous le doigt, ou -1. */
export function formeSous(
  formes: readonly Forme[],
  p: Point,
  tolerance: number,
  largeurTexte: (f: Forme) => number,
): number {
  for (let i = formes.length - 1; i >= 0; i--) {
    const f = formes[i]!
    if (touche(f, p, tolerance, largeurTexte(f))) return i
  }
  return -1
}

/** La même forme, décalée. */
export function deplacer(f: Forme, dx: number, dy: number): Forme {
  const points = f.points.map((p) => ({ x: p.x + dx, y: p.y + dy }))
  return f.outil === 'texte' ? { ...f, points: [points[0]!] } : { ...f, points }
}

/** La même forme, agrandie (k > 1) ou réduite autour du centre `c` (à plat). */
export function redimensionner(f: Forme, k: number, c: Point): Forme {
  const points = f.points.map((p) => ({ x: c.x + (p.x - c.x) * k, y: c.y + (p.y - c.y) * k }))
  return f.outil === 'texte'
    ? { ...f, points: [points[0]!], taille: Math.max(4, f.taille * k) }
    : { ...f, points }
}

/** Les angles droits et les diagonales « aimantent » à 3° près. */
export function aimanter(angle: number): number {
  const pas = Math.PI / 4
  const proche = Math.round(angle / pas) * pas
  return Math.abs(angle - proche) < (3 * Math.PI) / 180 ? proche : angle
}

/**
 * Poignées de la forme choisie, en pixels de la photo (orientation
 * comprise) : agrandir au coin bas droit, pivoter au-dessus du cadre.
 */
export function poignees(
  f: Forme,
  largeurTexte: number,
  marge: number,
  ecartRotation: number,
): { taille: Point; rotation: Point; attache: Point; centre: Point } {
  const b = boite(f, largeurTexte)
  const c = centre(f, largeurTexte)
  const angle = f.angle ?? 0
  return {
    centre: c,
    taille: tourner({ x: b.x + b.w + marge, y: b.y + b.h + marge }, c, angle),
    attache: tourner({ x: c.x, y: b.y - marge }, c, angle),
    rotation: tourner({ x: c.x, y: b.y - marge - ecartRotation }, c, angle),
  }
}
